import React, {useCallback, useMemo, useState} from "react";
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import Button from "../../../components/button/button.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import aggridUtils from "../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../utils/ag-grid-columns-utils.js";
import {navigateHook} from "../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import CompositionService from "../../../services/dispatch/composition-service.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;

const initialFilters = {name: ""};

const buildFilter = (values) => [
    {field: "name", type: "like", value: (values.name ?? "").replaceAll("'", "")},
];

const CompositionList = () => {
    const navigate = navigateHook();
    const [filterValues, setFilterValues] = useState(initialFilters);
    const [filter, setFilter] = useState([]);

    const {data = [], isLoading} = useQuery({
        queryFn: () => CompositionService.list({filter: [...filter]}),
        queryKey: ["dispatch-composition", {filter}],
        placeholderData: keepPreviousData,
    });

    const columnDefs = useMemo(() => [
        {field: "id", headerName: "ID", hide: true, cellDataType: "number"},
        {field: "name", headerName: "Nome", flex: 1},
        {field: "vehicleName", headerName: "Veículo", flex: 1},
    ], []);

    const handleInput = ({target: {id, value}}) => setFilterValues(prev => ({...prev, [id]: value}));

    const apply = useCallback(() => setFilter(buildFilter(filterValues)), [filterValues]);

    const clear = useCallback(() => {
        setFilterValues(initialFilters);
        setFilter([]);
    }, []);

    const onRowClicked = ({data: row}) => {
        if (!row?.id) return;
        navigate(`${API_DESPACHO_WEB}/composition/form/${row.id}`, true);
    };

    const openForm = () => navigate(API_DESPACHO_WEB + "/composition/form", false);

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Composições</Template.Title>
                <Button onClick={openForm}><FaPlus/>Adicionar composição</Button>
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Body className="flex justify-between gap-4">
                        <InputForm classNameWrapper="w-full" id="name" label="Nome" value={filterValues.name} onChange={handleInput}/>
                        <div className="flex gap-2 justify-end">
                            <Button onClick={clear} title="Limpar" target="primary-outline"><FaEraser/></Button>
                            <Button onClick={apply} title="Buscar"><FaSearch/></Button>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="h-[calc(100vh-153px)] aggrid-row-pointer">
                            <AgGridReact
                                rowData={data}
                                columnDefs={columnDefs}
                                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                pagination
                                onRowClicked={onRowClicked}
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default CompositionList;
